import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import RenderHTML from '../../../../components/ui-components/RenderHTML'
import BackButton from '../../../../components/ui-components/BackButton'
import { PrimaryButton } from '../../../../components/execution/PrimaryButton'
import { LoadingSpinner } from '../../../../components/LoadingSpinner'
import { SetupAutoPayMaxLimitBottomSheet } from '../../../../components/BottomSheetComponents/mf-execution/sip/SetupAutoPayMaxLimitBottomSheet'
import { PaymentNotCompletedBottomSheet } from '../../../../components/BottomSheetComponents/mf-execution/PaymentNotCompletedBottomSheet'
import { MUTUAL_FUND_DETAIL_IMAGE_URL } from '../../../../constants'
import { useInstalledUpiIntents } from '../../../../hooks/mf-execution/useInstalledUpiIntents'
import { fetchMandateDetails, setupAutoPay } from '../../../../repository/mfExecution'
import { useLoading } from '../../../../state-management/contexts/LoadingContext'
import { useBottomSheet } from '../../../../state-management/contexts/BottomSheetContext'
import {
  AutopayMethodSelection,
  AutoPaySetupStatus,
  SetupAutoPayPayload,
  SetupAutoPayResponse,
} from '../../../../types/mf-execution.types'
import { EventNames } from '../../../../types/misc.types'
import { goBack, postCTA, trackEvent } from '../../../../utils/appBridge'
import { parseAmountFromQueryParam } from '../../../../utils/mf-execution/paymentAmount'
import {
  buildAutoPayLinkWaitingPath,
  buildAutoPayStatusSearchParams,
  buildSetupAutoPayPayloadFromSelection,
  getAutopayModeLabel,
  getBankAccountsFromDetails,
  getMandateAccountsFromDetails,
  getSipDateDayFromMandateDetails,
  reconcileAutopayMethodSelection,
  resolveAutopayMaxLimit,
  resolveAutopayPaymentMode,
} from '../../../../utils/mf-execution/autopayMethodSelection'
import { getBankLogoSrc } from '../../../../utils/mf-execution/paymentMethodsScreenUtils'
import { getBottomPadding, getTopPadding } from '../../../../utils/sessionStorage'
import { roundOffAndFormatNumberWithCurrency } from '../../../../utils/utils'
import { getOrdersTabCTA } from '../../../../utils/mutualFundDetail'

type SetupAutoPayLocationState = {
  selectedAutopayMethod?: AutopayMethodSelection | null
}

/**
 * V2 autopay setup screen (UPI intents apps only).
 * Replaces the v1 bank list (`AutoPayMethodsPage`) with mandate-details-driven UI: mode/limit/date
 * summary, tappable payment method row → `AutopayMethodsScreen`, then setup + status polling.
 * Selection returns via `location.state` so query params (sxpId, amount) stay intact.
 */
export function SetupAutoPay() {
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const sxpId = searchParams.get('sxpId') ?? ''
  const amount = parseAmountFromQueryParam(searchParams)
  const fromApp = searchParams.get('from') === 'APP'
  const { showLoading, hideLoading } = useLoading()
  const { openBottomSheet, closeBottomSheet } = useBottomSheet()
  const { installedUpiApps, requestUpiIntents } = useInstalledUpiIntents()
  const [selectedAutopayMethod, setSelectedAutopayMethod] = useState<AutopayMethodSelection | null>(
    () => (location.state as SetupAutoPayLocationState | null)?.selectedAutopayMethod ?? null
  )

  const {
    data: mandateDetails,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['mandateDetails', sxpId],
    queryFn: () => fetchMandateDetails(sxpId),
    enabled: !!sxpId,
  })

  const mandateAccounts = useMemo(() => getMandateAccountsFromDetails(mandateDetails), [mandateDetails])
  const bankAccounts = useMemo(() => getBankAccountsFromDetails(mandateDetails), [mandateDetails])

  const defaultAutopayPaymentMode = resolveAutopayPaymentMode({
    mandateType: mandateDetails?.mandateType,
    amount,
  })
  const autopayPaymentMode = selectedAutopayMethod?.mode ?? defaultAutopayPaymentMode
  const isUpiMode = autopayPaymentMode === 'UPI'
  // Details card follows user's chosen method, not SIP-level mandateType alone.
  const modeLabel = getAutopayModeLabel(autopayPaymentMode)
  const maxLimitAmount = resolveAutopayMaxLimit({
    paymentMode: autopayPaymentMode,
    mandateType: mandateDetails?.mandateType,
    mandateMaxLimit: mandateDetails?.maxLimit,
  })
  const maxLimitLabel = roundOffAndFormatNumberWithCurrency(maxLimitAmount)
  const sipDateLabel = mandateDetails
    ? formatMonthlySipDateLabel(getSipDateDayFromMandateDetails(mandateDetails))
    : ''

  useEffect(() => {
    trackEvent({
      eventName: EventNames.ScreenLoaded,
      payload: {
        ScreenName: 'Setup Autopay',
      },
    })
  }, [])

  useEffect(() => {
    if (isUpiMode) {
      requestUpiIntents()
    }
  }, [isUpiMode, requestUpiIntents])

  useEffect(() => {
    if (!bankAccounts.length) return

    const returnedSelection = (location.state as SetupAutoPayLocationState | null)?.selectedAutopayMethod
    const reconciled = reconcileAutopayMethodSelection({
      selection: returnedSelection?.accountId ? returnedSelection : selectedAutopayMethod,
      mandateAccounts,
      bankAccounts,
      installedUpiApps,
      requiredMandateType: mandateDetails?.mandateType,
      amount,
    })

    if (!reconciled) return

    const hasSelectionChanged =
      selectedAutopayMethod?.accountId !== reconciled.accountId ||
      selectedAutopayMethod?.mode !== reconciled.mode ||
      selectedAutopayMethod?.upiPackageName !== reconciled.upiPackageName

    if (hasSelectionChanged) {
      setSelectedAutopayMethod(reconciled)
    }
  }, [
    amount,
    mandateAccounts,
    bankAccounts,
    installedUpiApps,
    mandateDetails?.mandateType,
    location.state,
    selectedAutopayMethod,
  ])

  const { mutate: setupAutoPayMethod, isPending: isSetupAutoPayPending } = useMutation({
    mutationFn: (data: SetupAutoPayPayload) => setupAutoPay(data),
    onSuccess: (data: SetupAutoPayResponse) => {
      hideLoading()
      const statusParams = buildAutoPayStatusSearchParams({
        sxpId: data?.sxpId,
        mandateId: data?.mandateId,
        autopayMethod: selectedAutopayMethod,
      })

      if (data?.status === AutoPaySetupStatus.SUCCESS) {
        navigate(`/sip/auto-pay/status?${statusParams.toString()}`)
      } else if (data?.status === AutoPaySetupStatus.INITIATED) {
        navigate(
          buildAutoPayLinkWaitingPath({
            sxpId: data?.sxpId,
            mandateId: data?.mandateId,
            autopayMethod: selectedAutopayMethod,
          })
        )
      }
    },
  })

  useEffect(() => {
    if (isSetupAutoPayPending) {
      showLoading()
    } else {
      hideLoading()
    }
  }, [isSetupAutoPayPending, showLoading, hideLoading])

  const handleSetupAutopayClick = () => {
    trackEvent({
      eventName: EventNames.ButtonTap,
      payload: {
        ScreenName: 'Setup Autopay',
        eventId: 'MFE_SETUP_AUTOPAY_CTA_SETUP',
        buttonLabel: 'Setup Autopay',
        amount,
        autopayMode: selectedAutopayMethod?.mode,
      },
    })

    if (!sxpId || !selectedAutopayMethod?.accountId) return

    const payload = buildSetupAutoPayPayloadFromSelection({
      sxpId,
      selection: selectedAutopayMethod,
      mandateAccounts,
      bankAccounts,
    })
    // mandateType from selection.mode; mandateId only when availableMandates matches mode.
    if (!payload) return

    setupAutoPayMethod(payload)
  }

  const handleChangePaymentMethodClick = () => {
    trackEvent({
      eventName: EventNames.UserClicked,
      payload: {
        ScreenName: 'Setup Autopay',
        eventId: 'MFE_SETUP_AUTOPAY_CHANGE_PAYMENT_METHOD',
        buttonLabel:
          selectedAutopayMethod?.mode === 'UPI'
            ? selectedAutopayMethod.upiAppName ?? 'UPI'
            : 'Net Banking',
        amount,
        autopayMode: selectedAutopayMethod?.mode,
      },
    })

    const params = new URLSearchParams(searchParams)
    if (!params.get('sxpId')) {
      params.set('sxpId', sxpId)
    }

    // Pass full query string so return navigation keeps sxpId/amount.
    navigate(`/v2/sip/auto-pay/methods/select?${params.toString()}`, {
      state: { selectedAutopayMethod },
    })
  }

  const handleMaxLimitClick = () => {
    openBottomSheet(
      <SetupAutoPayMaxLimitBottomSheet
        onDismiss={closeBottomSheet}
        mode={isUpiMode ? 'UPI' : 'NET_BANKING'}
        maxLimit={maxLimitAmount}
        ScreenName='Setup Autopay'
      />
    )
  }

  const handleBack = () => {
    openBottomSheet(
      <PaymentNotCompletedBottomSheet
        onDismiss={closeBottomSheet}
        onStayOnScreen={closeBottomSheet}
        onExitAnyway={() => {
          closeBottomSheet()
          if (fromApp) {
            goBack()
            return
          }
          postCTA(JSON.stringify(getOrdersTabCTA()))
        }}
        ScreenName='Setup Autopay'
        isAutopay
      />
    )
  }

  if (!sxpId) {
    return (
      <div className='flex flex-col items-center justify-center min-h-screen bg-white p-6'>
        <p className='text-[#64748b] text-center text-[16px]'>Missing SIP details. Please try again.</p>
      </div>
    )
  }

  if (isLoading) {
    return <LoadingSpinner />
  }

  if (isError || !bankAccounts.length) {
    return (
      <div className='flex flex-col items-center justify-center min-h-screen bg-white p-6'>
        <div className='text-[#64748b] text-center'>
          <div className='text-[20px] font-bold mb-2 text-[#334155]'>No Autopay Methods</div>
          <div className='text-[16px] text-[#475569]'>Add a bank account to enable Autopay</div>
        </div>
      </div>
    )
  }

  if (!selectedAutopayMethod?.accountId) {
    return <LoadingSpinner />
  }

  const paymentMethodTitle =
    selectedAutopayMethod.mode === 'UPI'
      ? selectedAutopayMethod.upiAppName ?? 'UPI'
      : 'Net Banking'
  const paymentMethodLogo =
    selectedAutopayMethod.mode === 'UPI'
      ? selectedAutopayMethod.upiAppLogoUrl ?? ''
      : selectedAutopayMethod.bankUrlLogo ?? ''

  return (
    <div
      style={{
        paddingTop: `calc(${getTopPadding()}px + 64px)`,
        paddingBottom: `calc(${getBottomPadding()}px + 12px)`,
      }}
      className='h-screen w-full bg-white flex flex-col relative'
    >
      <div style={{ top: `${getTopPadding() + 12}px` }} className='absolute left-5 z-10'>
        <BackButton onClick={handleBack} />
      </div>

      <div className='flex flex-col flex-1 w-full overflow-y-auto no-scrollbar min-h-0 pb-32'>
        <div className='flex flex-col pb-6 px-5 flex-shrink-0'>
          <div className='pb-3'>
            <RenderHTML innerHTMLText={AutoPayIcon} />
          </div>
          <div className='text-[#232323] font-[500] leading-[150%] text-[22px] text-left'>
            Add Autopay for your SIP
          </div>
          <div className='text-[#6A6E6E] font-[400] leading-[150%] text-[14px] text-left pt-2 pb-1'>
            Add Autopay so that your SIPs are debited <br /> automatically every month
          </div>
        </div>

        <div className='px-5 flex flex-col gap-4'>
          <SetupAutoPayDetailsCard
            modeLabel={modeLabel}
            maxLimitLabel={maxLimitLabel as string}
            sipDateLabel={sipDateLabel}
            onMaxLimitClick={handleMaxLimitClick}
          />
        </div>
      </div>

      <div
        className='absolute bottom-0 left-0 right-0 z-20 bg-white px-5 pt-4'
        style={{ paddingBottom: `calc(${getBottomPadding()}px + 16px)` }}
      >
        <SetupAutoPayPaymentMethodRow
          imageSrc={paymentMethodLogo}
          imageAlt={paymentMethodTitle}
          title={paymentMethodTitle}
          subtitle={selectedAutopayMethod.displayAccNumber ?? ''}
          onClick={handleChangePaymentMethodClick}
        />
        <PrimaryButton
          primaryButtonClassName='!text-[16px] !h-14 !mt-4'
          onClick={handleSetupAutopayClick}
          label='Setup Autopay'
        // isDisabled={!selectedAutopayMethod?.mandateId}
        />
        {isUpiMode ? (
          <p className='text-[#6A6E6E] text-[12px] font-[400] leading-[150%] text-center mt-3'>
            ₹1 will be debited for set up • fully refundable
          </p>
        ) : null}
      </div>
    </div>
  )
}

function formatMonthlySipDateLabel(day: number) {
  return `${day}${getDaySuffix(day)} of every month`
}

function getDaySuffix(day: number) {
  if (day >= 11 && day <= 13) return 'th'
  switch (day % 10) {
    case 1:
      return 'st'
    case 2:
      return 'nd'
    case 3:
      return 'rd'
    default:
      return 'th'
  }
}

export function SetupAutoPayDetailsCard({
  modeLabel,
  maxLimitLabel,
  sipDateLabel,
  onMaxLimitClick,
}: {
  modeLabel: string
  maxLimitLabel: string
  sipDateLabel: string
  onMaxLimitClick?: () => void
}) {
  return (
    <div className='border border-[#F0F4F7] rounded-[16px] w-full px-4 py-1 bg-white'>
      <SetupAutoPayDetailRow label='Mode' value={modeLabel} isLast />
      <SetupAutoPayDetailRow
        label='Max. limit'
        value={maxLimitLabel}
        showInfoIcon
        onClick={onMaxLimitClick}
        isLast
      />
      <SetupAutoPayDetailRow label='SIP date' value={sipDateLabel} isLast />
    </div>
  )
}

export function SetupAutoPayDetailRow({
  label,
  value,
  showInfoIcon = false,
  isLast = false,
  onClick,
}: {
  label: string
  value: string
  showInfoIcon?: boolean
  isLast?: boolean
  onClick?: () => void
}) {
  return (
    <div
      className={`flex items-center justify-between py-2 ${isLast ? '' : 'border-b border-[#F0F4F7]'} ${onClick ? 'cursor-pointer' : ''}`}
      onClick={onClick}
    >
      <div className='flex items-center gap-1.5'>
        <span className='text-[#6A6E6E] text-[14px] font-[400] leading-[150%]'>{label}</span>
        {showInfoIcon ? (
          <img
            src={`${MUTUAL_FUND_DETAIL_IMAGE_URL}/info-icon.svg`}
            alt=''
            className='w-3 h-3'
          />
        ) : null}
      </div>
      <span className='text-[#232323] text-[14px] font-[500] leading-[150%]'>{value}</span>
    </div>
  )
}

export function SetupAutoPayPaymentMethodRow({
  imageSrc,
  imageAlt,
  title,
  subtitle,
  onClick,
}: {
  imageSrc: string
  imageAlt: string
  title: string
  subtitle: string
  onClick?: () => void
}) {
  return (
    <div className='border border-[#F0F4F7] rounded-[16px] w-full bg-white'>
      <div
        className={`flex items-center w-full pl-3 pr-4 py-3 ${onClick ? 'cursor-pointer' : ''}`}
        onClick={onClick}
      >
        {imageSrc ? (
          <div className='flex items-center mr-3 flex-shrink-0'>
            <img src={getBankLogoSrc(imageSrc)} alt={imageAlt} className='w-8 h-8 object-contain' />
          </div>
        ) : null}
        <div className='flex flex-col w-1 flex-1 min-w-0'>
          <div className='text-[13px] truncate max-w-[90%] text-[#232323] font-[500]'>{title}</div>
          <div className='text-[13px] truncate max-w-[90%] text-[#6A6E6E] font-[400]'>{subtitle}</div>
        </div>
        <div className='flex-shrink-0 ml-2'>
          <RenderHTML innerHTMLText={RightChevron} />
        </div>
      </div>
    </div>
  )
}

const AutoPayIcon = `<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 48 48" fill="none">
  <path fill-rule="evenodd" clip-rule="evenodd" d="M24 7.50878C14.8844 7.50878 7.65213 14.9579 7.65213 24C7.65213 27.2102 8.55466 30.1728 10.1327 32.7133L12.6025 30.2219C12.8512 29.9711 13.2252 29.896 13.5502 30.0318C13.8751 30.1676 14.087 30.4874 14.087 30.8421V39.2632C14.087 39.7477 13.6977 40.1404 13.2174 40.1404H4.86958C4.51788 40.1404 4.20081 39.9267 4.06622 39.599C3.93162 39.2711 4.00602 38.8939 4.25471 38.643L7.62071 35.2474C5.44727 32.0355 4.17387 28.1934 4.17387 24C4.17387 13.0421 12.9416 4 24 4C26.6727 4 29.3342 4.57545 31.6254 5.53844C32.512 5.91109 32.9313 6.93825 32.5619 7.83263C32.1923 8.72702 31.1742 9.14997 30.2876 8.7773C28.4047 7.9859 26.1968 7.50878 24 7.50878ZM34.7826 9.26311H43.1304C43.482 9.26311 43.7993 9.47683 43.9339 9.8046C44.0683 10.1324 43.994 10.5097 43.7453 10.7606L40.8175 13.7139C42.6159 16.7203 43.6521 20.2172 43.6521 24C43.6521 34.9579 34.8845 44 23.8261 44C21.1534 44 18.4918 43.4246 16.2007 42.4616C15.3141 42.0888 14.8948 41.0618 15.2642 40.1674C15.6337 39.273 16.6519 38.85 17.5385 39.2226C19.4213 40.014 21.6292 40.4912 23.8261 40.4912C32.9417 40.4912 40.1739 33.0421 40.1739 24C40.1739 21.2023 39.4885 18.5925 38.2676 16.2863L35.3975 19.1816C35.1488 19.4325 34.7747 19.5075 34.4499 19.3718C34.1248 19.236 33.913 18.9161 33.913 18.5613V10.1403C33.913 9.65583 34.3024 9.26311 34.7826 9.26311ZM22.7085 24.2839L24 20.3754L25.2915 24.2839H22.7085ZM26.9796 18.2971L29.3415 25.4446C29.3506 25.4702 29.3591 25.4958 29.3669 25.522L30.7014 29.5602C31.0052 30.4793 30.5127 31.473 29.6015 31.7793C28.6902 32.0856 27.7054 31.589 27.4015 30.6697L26.4509 27.7927H21.549L20.5984 30.6697C20.2946 31.589 19.3097 32.0856 18.3985 31.7793C17.4873 31.473 16.9949 30.4793 17.2986 29.5602L18.633 25.522C18.6409 25.4958 18.6494 25.4702 18.6585 25.4446L21.0203 18.2971C21.4478 17.0033 22.6481 16.1306 24 16.1306C25.352 16.1306 26.5521 17.0033 26.9796 18.2971Z" fill="#1470CC"/>
</svg>`

const RightChevron = `<svg xmlns="http://www.w3.org/2000/svg" width="8" height="14" viewBox="0 0 8 14" fill="none">
  <path d="M1.0957 13.0001L6.7693 7.32648C6.94958 7.14624 6.94958 6.85401 6.7693 6.67377L1.0957 1.00012" stroke="#232323" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
</svg>`
